import { useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../ui";
import {
  MACRO_EM_BRANCO,
  corpoDaMacro,
  formularioDaMacro,
  usePlaceholders,
  useAlterarMacro,
  useArquivarMacro,
  useCriarMacro,
  useMacros,
  validarMacro,
  type FormularioDeMacro,
  type Macro,
} from "../../nucleo/automacao";
import { mensagemDoConsole } from "../../nucleo/console";
import { podeNaSessao, useSessao } from "../../nucleo/sessao";
import {
  ROTULO_DA_URGENCIA,
  ROTULO_DO_ESTADO,
  useAgentesDeAtendimento,
  useEquipesDeAtendimento,
} from "../../nucleo/tickets";
import { CampoDeEscolha, CaixaDeMarcar } from "./apoio";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";

const OPCOES_DE_ESTADO = Object.entries(ROTULO_DO_ESTADO).map(([valor, rotulo]) => ({ valor, rotulo }));
const OPCOES_DE_URGENCIA = Object.entries(ROTULO_DA_URGENCIA).map(([valor, rotulo]) => ({ valor, rotulo }));

export function PainelDeMacros() {
  const sessao = useSessao();
  const administra = podeNaSessao(sessao.data, "automacao:gerenciar");
  const macros = useMacros();
  const marcadores = usePlaceholders();
  const equipes = useEquipesDeAtendimento();
  const agentes = useAgentesDeAtendimento();
  const criar = useCriarMacro();
  const voltarParaHome = useVoltarParaHome();
  const alterar = useAlterarMacro();
  const arquivar = useArquivarMacro();
  const [form, setForm] = useState<FormularioDeMacro>(MACRO_EM_BRANCO);
  const [emEdicao, setEmEdicao] = useState<string | null>(null);
  const [problema, setProblema] = useState<string | null>(null);

  const atualizar = <C extends keyof FormularioDeMacro>(campo: C, valor: FormularioDeMacro[C]) =>
    setForm((atual) => ({ ...atual, [campo]: valor }));

  function limpar() {
    setForm(MACRO_EM_BRANCO);
    setEmEdicao(null);
    setProblema(null);
  }

  function editar(macro: Macro) {
    setForm(formularioDaMacro(macro));
    setEmEdicao(macro.id);
    setProblema(null);
  }

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    const erro = validarMacro(form);
    setProblema(erro);
    if (erro) return;
    const corpo = corpoDaMacro(form);
    if (emEdicao) alterar.mutate({ id: emEdicao, corpo }, { onSuccess: limpar });
    else {
      criar.mutate(corpo, {
        onSuccess: () => {
          limpar();
          voltarParaHome("Macro criada.");
        },
      });
    }
  }

  const falha = criar.error ?? alterar.error ?? arquivar.error ?? macros.error;
  const gravando = criar.isPending || alterar.isPending;

  return (
    <div>
      <Cartao titulo={emEdicao ? "Editar macro" : "Nova macro"} className="mb-4">
        <form onSubmit={submeter} data-testid="formulario-de-macro" noValidate>
          <Campo rotulo="Nome da macro" value={form.nome} onChange={(e) => atualizar("nome", e.target.value)} />
          <CampoLongo
            rotulo="Texto da resposta"
            ajuda="Opcional se a macro só muda campos do chamado."
            value={form.texto}
            onChange={(e) => atualizar("texto", e.target.value)}
          />
          {marcadores.data && marcadores.data.length > 0 ? (
            <div className="mb-3" data-testid="marcadores-da-macro">
              <p className="campo__rotulo">Marcadores (clique para inserir no texto)</p>
              <div className="flex gap-2 flex-wrap">
                {marcadores.data.map((marcador) => (
                  <Botao
                    key={marcador.chave}
                    type="button"
                    variante="fantasma"
                    tamanho="pequeno"
                    title={marcador.descricao}
                    data-testid={`marcador-${marcador.chave}`}
                    onClick={() => atualizar("texto", `${form.texto}{{${marcador.chave}}}`)}
                  >
                    {`{{${marcador.chave}}}`}
                  </Botao>
                ))}
              </div>
            </div>
          ) : null}
          <CampoDeEscolha
            rotulo="Tipo de resposta"
            valor={form.tipo_de_resposta}
            aoMudar={(v) => atualizar("tipo_de_resposta", v as FormularioDeMacro["tipo_de_resposta"])}
            opcoes={[
              { valor: "publica", rotulo: "Resposta pública ao cliente" },
              { valor: "interna", rotulo: "Nota interna" },
            ]}
          />
          <div className="flex gap-3 flex-wrap">
            <CampoDeEscolha rotulo="Mudar estado para" className="flex-1" valor={form.estado} aoMudar={(v) => atualizar("estado", v)} opcoes={OPCOES_DE_ESTADO} vazio="Não mudar" />
            <CampoDeEscolha rotulo="Mudar urgência para" className="flex-1" valor={form.urgencia} aoMudar={(v) => atualizar("urgencia", v)} opcoes={OPCOES_DE_URGENCIA} vazio="Não mudar" />
            <CampoDeEscolha rotulo="Atribuir à equipe" className="flex-1" valor={form.equipe_id} aoMudar={(v) => atualizar("equipe_id", v)} opcoes={(equipes.data ?? []).map((e) => ({ valor: e.id, rotulo: e.nome }))} vazio="Não mudar" />
            <CampoDeEscolha rotulo="Atribuir ao responsável" className="flex-1" valor={form.responsavel_id} aoMudar={(v) => atualizar("responsavel_id", v)} opcoes={(agentes.data ?? []).map((a) => ({ valor: a.id, rotulo: a.nome }))} vazio="Não mudar" />
          </div>
          <div className="flex gap-3 flex-wrap">
            <Campo rotulo="Mudar categoria para" value={form.categoria} onChange={(e) => atualizar("categoria", e.target.value)} />
            <Campo rotulo="Mudar serviço para" value={form.servico} onChange={(e) => atualizar("servico", e.target.value)} />
          </div>
          <div className="flex gap-3 flex-wrap">
            <Campo
              rotulo="Adicionar as tags"
              ajuda="Separe por vírgula. As tags são internas: o cliente nunca as vê."
              value={form.adicionar_tags}
              onChange={(e) => atualizar("adicionar_tags", e.target.value)}
            />
            <Campo rotulo="Retirar as tags" ajuda="Separe por vírgula." value={form.remover_tags} onChange={(e) => atualizar("remover_tags", e.target.value)} />
          </div>
          {administra ? (
            <CaixaDeMarcar
              rotulo="Compartilhar com toda a equipe"
              marcada={form.compartilhada}
              aoMudar={(v) => atualizar("compartilhada", v)}
              ajuda="Sem marcar, só você vê e usa esta macro."
            />
          ) : null}
          {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
          {falha ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(falha)}</Aviso> : null}
          <div className="flex gap-2">
            <Botao type="submit" disabled={gravando}>
              {gravando ? "Salvando…" : emEdicao ? "Salvar macro" : "Criar macro"}
            </Botao>
            {emEdicao ? (
              <Botao type="button" variante="fantasma" onClick={limpar}>
                Cancelar edição
              </Botao>
            ) : null}
          </div>
        </form>
      </Cartao>

      <section aria-labelledby="titulo-macros">
        <h2 className="titulo-secao" id="titulo-macros">Macros</h2>
        {macros.isPending ? <p role="status">Carregando macros…</p> : null}
        {macros.data && macros.data.length === 0 ? <Aviso tom="marca">Nenhuma macro ainda.</Aviso> : null}
        <div className="lista-de-itens" data-testid="lista-de-macros">
          {macros.data?.map((macro) => {
            const pode = macro.minha || (macro.compartilhada && administra);
            return (
              <Cartao
                key={macro.id}
                titulo={macro.nome}
                acessorio={<Chip estado={macro.compartilhada ? "ok" : "neutro"}>{macro.compartilhada ? "Compartilhada" : "Pessoal"}</Chip>}
                data-testid={`macro-${macro.nome}`}
              >
                <p className="texto-preservado">{macro.texto ?? "Só muda campos do chamado."}</p>
                <p className="nota-rodape mt-2">
                  {macro.tipo_de_resposta === "interna" ? "Nota interna" : "Resposta pública"}
                  {Object.keys(macro.mudancas).length > 0 ? ` · muda ${Object.keys(macro.mudancas).length} campo(s)` : ""}
                </p>
                {pode ? (
                  <div className="flex gap-2 mt-2">
                    <Botao type="button" variante="fantasma" onClick={() => editar(macro)} aria-label={`Editar macro ${macro.nome}`}>Editar</Botao>
                    <Botao type="button" variante="fantasma" onClick={() => arquivar.mutate(macro.id)} aria-label={`Arquivar macro ${macro.nome}`}>Arquivar</Botao>
                  </div>
                ) : null}
              </Cartao>
            );
          })}
        </div>
      </section>
    </div>
  );
}
